import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import ChatRoom from "./ChatRoom";

export default function ChatRoomWrapper() {
  const { chatId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  const hasSentAutoMessage = useRef(false);

  // Buscar info del usuario o perfil promocional
  const fetchUserInfo = async (otherUserId, isPromotional) => {
    if (!isPromotional) {
      // Usuario normal
      const userDoc = await getDoc(doc(db, "users", otherUserId));
      if (userDoc.exists()) {
        return { uid: otherUserId, isPromo: false, ...userDoc.data() };
      }
    } else {
      // Usuario promocional -> buscar en promoProfiles de todos los usuarios
      const usersSnap = await getDocs(collection(db, "users"));
      for (const userDoc of usersSnap.docs) {
        const promoProfileRef = doc(
          db,
          "users",
          userDoc.id,
          "promoProfiles",
          otherUserId
        );
        const promoSnap = await getDoc(promoProfileRef);
        if (promoSnap.exists()) {
          return { uid: otherUserId, isPromo: true, ...promoSnap.data() };
        }
      }
    }
    return null;
  };

  useEffect(() => {
    const fetchChatAndUser = async () => {
      if (!chatId || !currentUser?.uid) return;

      try {
        // 1️⃣ Intentar buscar en "chats"
        let chatDoc = await getDoc(doc(db, "chats", chatId));
        let isPromotional = false;
        let collectionName = "chats";

        // 2️⃣ Si no existe, intentar en "chatsPromotional"
        if (!chatDoc.exists()) {
          chatDoc = await getDoc(doc(db, "chatsPromotional", chatId));
          isPromotional = true;
          collectionName = "chatsPromotional";
        }

        if (!chatDoc.exists()) {
          console.warn("Chat no encontrado en ninguna colección");
          setLoading(false);
          return;
        }

        const chatData = chatDoc.data();
        const usersInChat = chatData.users || [];

        // ── Verificación de acceso ────────────────────────────────────────────
        // El usuario actual DEBE ser participante del chat.
        // Si alguien pega un enlace ajeno, lo mandamos al home silenciosamente.
        if (!usersInChat.includes(currentUser.uid)) {
          console.warn("[ChatRoomWrapper] Acceso denegado: usuario no es participante.");
          navigate("/", { replace: true });
          return;
        }

        const otherUserId = usersInChat.find((id) => id !== currentUser.uid);

        if (!otherUserId) {
          console.warn("No se encontró otro usuario en el chat");
          setLoading(false);
          return;
        }

        // 3️⃣ Obtener info del otro usuario
        const userInfo = await fetchUserInfo(otherUserId, isPromotional);

        if (userInfo) {
          setOtherUser(userInfo);
        } else {
          console.warn("Usuario no encontrado en users ni promoProfiles");
        }
      } catch (err) {
        console.error("Error obteniendo chat o usuario:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchChatAndUser();
  }, [chatId, currentUser]);

  // Enviar mensaje automático si existe el query param
  useEffect(() => {
    const sendAutoMessage = async () => {
      if (!currentUser || !chatId) return;

      const autoMessage = searchParams.get("autoMessage");
      if (!autoMessage) return;
      if (hasSentAutoMessage.current) return;
      hasSentAutoMessage.current = true;

      try {
        // 1️⃣ Intentar en "chats"
        let messagesRef = collection(db, "chats", chatId, "messages");
        let chatDoc = await getDoc(doc(db, "chats", chatId));

        // 2️⃣ Si no existe, usar "chatsPromotional"
        if (!chatDoc.exists()) {
          messagesRef = collection(db, "chatsPromotional", chatId, "messages");
          chatDoc = await getDoc(doc(db, "chatsPromotional", chatId));
        }

        if (!chatDoc.exists()) {
          console.warn("No se encontró chat para enviar mensaje automático");
          return;
        }

        await addDoc(messagesRef, {
          text: decodeURIComponent(autoMessage),
          sender: currentUser.uid,
          createdAt: serverTimestamp(),
          read: false,
          attachments: [],
        });
      } catch (error) {
        console.error("Error enviando mensaje automático:", error);
      }
    };

    sendAutoMessage();
  }, [chatId, currentUser, searchParams]);

  if (loading) {
    return (
      <div className="p-4 text-gray-600 dark:text-gray-300 text-center">
        Cargando usuario...
      </div>
    );
  }

  if (!otherUser) {
    return (
      <div className="p-4 text-red-600 text-center">Usuario no encontrado.</div>
    );
  }

  return <ChatRoom otherUser={otherUser} onBack={() => navigate(-1)} />;
}
